import {
  ArchiveIcon,
  Avatar,
  Button,
  ChatRow,
  cn,
  EmptyState,
  FilterTabs,
  IconButton,
  MessageIcon,
  PageHeader,
  PenIcon,
  PlusIcon,
  SearchField,
  Tag,
  UsersIcon,
} from "@rhema/ui";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { NewChatSheet } from "../chats/NewChatSheet";
import { draftPreview, useDrafts } from "../conversation/drafts";
import { type ChatView, chatView, initialsFor, listTime, unreadCount } from "../data/view";
import { useMenus } from "../menus/actions";
import { muteEndLabel } from "../menus/mute";
import { WithMenu } from "../menus/WithMenu";
import { NotificationPrompt } from "../notifications/NotificationPrompt";
import { useQueravelOffered } from "../queravel/QueravelChat";
import { QueravelTile } from "../queravel/QueravelParts";
import { useQueravelWriting } from "../queravel/useQueravel";
import { usePhoneEnabled } from "../session/features";
import { useClientState } from "../session/session";
import { StarredMessages } from "./StarredMessages";

export function ChatAvatar({
  chat,
  size,
  ringClassName,
  preferInitials = false,
}: {
  chat: Pick<ChatView, "kind" | "title" | "tone" | "online"> & { photo?: string | null };
  size: number;
  ringClassName?: string;
  preferInitials?: boolean;
}) {
  if (chat.kind === "channel") {
    return (
      <Avatar
        name={chat.title}
        src={chat.photo}
        initials={initialsFor(chat.title)}
        tone={chat.tone}
        size={size}
        shape="rounded"
      />
    );
  }
  if (chat.kind === "group") {
    return (
      <Avatar
        name={chat.title}
        src={chat.photo}
        initials={initialsFor(chat.title)}
        tone={chat.tone}
        size={size}
        shape="rounded"
        icon={preferInitials ? undefined : <UsersIcon size={Math.round(size * 0.44)} />}
      />
    );
  }
  return (
    <Avatar
      name={chat.title}
      src={chat.photo}
      tone={chat.tone}
      size={size}
      online={chat.online}
      ringClassName={ringClassName}
    />
  );
}

/** What a chat is, under its name at the top of its phone menu. */
function kindLabel(chat: ChatView): string {
  if (chat.kind === "self") return "Your own chat";
  if (chat.kind === "group") {
    const n = chat.chat.member_count;
    return `Group · ${n} ${n === 1 ? "member" : "members"}`;
  }
  return "Direct chat";
}

type Filter = "all" | "unread" | "groups" | "pinned" | "archived" | "favourites";

function applyFilter(chats: ChatView[], filter: Filter, query: string) {
  const q = query.trim().toLowerCase();
  return chats.filter((c) => {
    if (q && !`${c.title} ${c.preview} ${c.sender ?? ""}`.toLowerCase().includes(q)) return false;
    if (filter === "archived") return c.archived;
    if (c.archived) return false;
    if (filter === "unread") return c.unread > 0 || c.markedUnread;
    if (filter === "groups") return c.kind === "group";
    if (filter === "pinned") return c.pinned;
    if (filter === "favourites") return c.chat.starred === true;
    return true;
  });
}

export function useChatViews() {
  const state = useClientState();
  return useMemo(
    () =>
      state.chatOrder
        .map((id) => state.chats[id])
        .filter((c) => c !== undefined && c.kind !== "assistant" && c.kind !== "channel")
        .map((c) => chatView(c as NonNullable<typeof c>, state)),
    [state],
  );
}

/** The Queravel row: the person's own Queravel chat, kept apart from their other chats. */
function useQueravelRow() {
  const state = useClientState();
  return useMemo(() => {
    const chat = Object.values(state.chats).find((c) => c.kind === "assistant");
    return {
      title: "Queravel",
      time: chat?.last_message ? listTime(chat.last_message_at) : "",
      preview: (chat && state.previews[chat.id]) || "Ask questions about your chats",
    };
  }, [state]);
}

export function ChatList({ variant, selectedId }: { variant: "phone" | "desktop"; selectedId?: string }) {
  const chats = useChatViews();
  const offered = useQueravelOffered();
  const queravel = useQueravelRow();
  const writing = useQueravelWriting();
  const menus = useMenus();
  const assistantChat = Object.values(useClientState().chats).find((c) => c.kind === "assistant");
  const phone = usePhoneEnabled();
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState("");
  const [newChat, setNewChat] = useState(false);
  const filter = (params.get("filter") as Filter | null) ?? "all";
  const desktop = variant === "desktop";
  // What you started writing in a chat shows in its row until it is sent, except in the chat open
  // beside the list, where the box itself shows it.
  const drafts = useDrafts();
  const draftFor = (chatId: string) => (chatId === selectedId ? undefined : draftPreview(drafts.get(chatId)));

  const setFilter = (next: Filter) => {
    const p = new URLSearchParams(params);
    if (next === "all") p.delete("filter");
    else p.set("filter", next);
    setParams(p, { replace: true });
  };

  const active = chats.filter((c) => !c.archived);
  const archived = chats.filter((c) => c.archived);
  // A chat marked unread counts here until it is opened, as one with new messages does.
  const unreadChats = active.filter((c) => c.unread > 0 || c.markedUnread).length;
  const rows = applyFilter(chats, filter, query);
  const showArchivedEntry = filter === "all" && !query && archived.length > 0;
  // On phones this row is how Queravel is reached. Desktop opens it from the rail and ⌘J instead,
  // so the list holds only real chats there.
  const showQueravel = !desktop && offered && filter === "all" && !query;

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <PageHeader
        title={filter === "archived" ? "Archived" : filter === "favourites" ? "Starred" : "Chats"}
        size={desktop ? "md" : "lg"}
        actions={
          desktop ? (
            <Button
              size="sm"
              icon={<PlusIcon size={15} strokeWidth={2.6} />}
              onClick={() => setNewChat(true)}
            >
              New chat
            </Button>
          ) : undefined
        }
      />
      <NotificationPrompt />
      <SearchField
        label={desktop ? "Search" : "Search chats"}
        placeholder="Search chats"
        value={query}
        onChange={setQuery}
        size={desktop ? "sm" : "md"}
        className="mx-5"
      />
      {filter === "archived" ? (
        <div className="px-5 pt-3.5 pb-1.5">
          <Button variant="ghost" size="sm" onClick={() => setFilter("all")}>
            Back to all chats
          </Button>
        </div>
      ) : (
        <FilterTabs
          label="Filter chats"
          size={desktop ? "sm" : "md"}
          value={filter}
          onChange={setFilter}
          className={cn(
            "px-5",
            desktop
              ? "flex-wrap gap-1.5 pt-3.5 pb-2"
              : "overflow-x-auto pt-3.5 pb-1.5 [scrollbar-width:none]",
          )}
          options={[
            { id: "all", label: "All" },
            { id: "unread", label: "Unread", count: unreadChats },
            { id: "groups", label: "Groups" },
            { id: "pinned", label: "Pinned" },
            { id: "favourites", label: "Starred" },
          ]}
        />
      )}
      <ul
        aria-label="Chats"
        className={cn(
          "m-0 flex min-h-0 flex-1 list-none flex-col overflow-y-auto p-0",
          desktop ? "gap-0.5 px-2.5 py-1" : "pt-1.5 pb-24",
        )}
      >
        {showArchivedEntry && (
          <WithMenu
            label="Archived"
            items={() => menus.archived(archived.map((c) => c.chat))}
            heading={{
              title: "Archived",
              detail: `${archived.length} ${archived.length === 1 ? "chat" : "chats"}`,
              icon: (
                <span className="flex h-11 w-11 flex-none items-center justify-center rounded-full bg-surface-header text-ink-secondary">
                  <ArchiveIcon size={20} />
                </span>
              ),
            }}
          >
            <ChatRow
              onClick={() => setFilter("archived")}
              density={desktop ? "compact" : "comfortable"}
              title="Archived"
              avatar={
                <span
                  className={cn(
                    "flex flex-none items-center justify-center rounded-full bg-surface-header text-ink-secondary",
                    desktop ? "h-[46px] w-[46px]" : "h-[52px] w-[52px]",
                  )}
                >
                  <ArchiveIcon size={20} />
                </span>
              }
              time=""
              preview={`${archived.length} ${archived.length === 1 ? "chat" : "chats"}`}
              unread={archived.reduce((n, c) => n + unreadCount(c.chat), 0)}
            />
          </WithMenu>
        )}
        {showQueravel && (
          <WithMenu label="Queravel" items={() => menus.queravel(assistantChat)}>
            <ChatRow
              variant="card"
              density="comfortable"
              href="/queravel"
              title={queravel.title}
              tag={<Tag className="bg-queravel-tint text-queravel">Assistant</Tag>}
              avatar={<QueravelTile size={52} />}
              time={queravel.time}
              preview={
                writing ? (
                  <span className="truncate italic text-blue">Queravel is writing…</span>
                ) : (
                  queravel.preview
                )
              }
            />
          </WithMenu>
        )}
        {rows.map((chat) => (
          <WithMenu
            key={chat.id}
            label={chat.title}
            items={() => menus.chat(chat.chat)}
            heading={{
              title: chat.title,
              detail: kindLabel(chat),
              icon: <ChatAvatar chat={chat} size={44} />,
            }}
          >
            <ChatRow
              href={`/chats/${chat.id}`}
              density={desktop ? "compact" : "comfortable"}
              selected={chat.id === selectedId}
              title={chat.title}
              avatar={
                <ChatAvatar
                  chat={chat}
                  size={desktop ? 46 : 52}
                  preferInitials={desktop}
                  ringClassName={chat.id === selectedId ? "border-surface-header" : "border-linen"}
                />
              }
              time={chat.time}
              preview={chat.preview}
              sender={chat.sender}
              typing={chat.typing}
              draft={draftFor(chat.id)}
              unread={chat.unread}
              markedUnread={chat.markedUnread}
              mention={chat.mention}
              muted={chat.muted}
              mutedUntil={chat.muted ? muteEndLabel(chat.chat.muted_until) : undefined}
              pinned={chat.pinned}
              status={chat.status}
            />
          </WithMenu>
        ))}
        {rows.length === 0 && filter === "favourites" && !query && (
          <li className="px-5 pt-3 text-[14px] leading-normal text-ink-secondary">
            Star a chat from its menu to keep it here.
          </li>
        )}
        {filter === "favourites" && !query && (
          <li>
            <StarredMessages desktop={desktop} />
          </li>
        )}
        {rows.length === 0 && filter !== "favourites" && (
          <li>
            <EmptyState
              icon={<MessageIcon size={26} />}
              title={chats.length === 0 ? "No chats yet" : "No chats here"}
              action={
                chats.length === 0 ? (
                  <Button onClick={() => setNewChat(true)} icon={<PenIcon size={16} />}>
                    Start a chat
                  </Button>
                ) : undefined
              }
            >
              {chats.length === 0
                ? phone
                  ? "Find someone from your contacts or by their phone number and say hello."
                  : "Find someone by their email address and say hello."
                : filter === "archived"
                  ? "Chats you archive will show here."
                  : "Try another filter or search."}
            </EmptyState>
          </li>
        )}
      </ul>
      {!desktop && (
        <IconButton
          label="New chat"
          variant="primary"
          size={58}
          onClick={() => setNewChat(true)}
          className="absolute right-5 bottom-5 rounded-[18px] shadow-fab"
        >
          <PenIcon size={25} strokeWidth={2.2} />
        </IconButton>
      )}
      <NewChatSheet open={newChat} onClose={() => setNewChat(false)} />
    </div>
  );
}
